/**
 * PrePublishMigrationModal.tsx — T17 pre-publish migration warning modal.
 *
 * Shown before a website/site-builder publish when the migration-check preview
 * detects ranking URLs that would disappear after the publish. Built on GlassModal.
 *
 * Design decisions (locked in spec):
 * - "Publish anyway" is `variant="primary"` — proceeding is a valid deliberate choice.
 * - "Cancel" is `variant="secondary"` — never ghost for a text Cancel button (AIME gotcha).
 * - Fail-open: if this modal never opens (preview errored), publish proceeds unchanged.
 * - Redirect suggestions are advisory only — copy-to-clipboard, never auto-applied.
 */
import React from 'react';
import { AlertCircle } from 'lucide-react';
import { GlassModal, GlassButton } from '../ui';
import { MigrationPreviewSummary } from '../../services/api';

// ── Props ─────────────────────────────────────────────────────────────────────

export interface PrePublishMigrationModalProps {
    isOpen: boolean;
    summary: MigrationPreviewSummary | null;   // null while loading
    loading: boolean;
    onProceed: () => void;   // user clicked "Publish anyway"
    onCancel: () => void;    // user clicked "Cancel"
}

// ── Component ─────────────────────────────────────────────────────────────────

const PrePublishMigrationModal: React.FC<PrePublishMigrationModalProps> = ({
    isOpen,
    summary,
    loading,
    onProceed,
    onCancel,
}) => {
    const footer = (
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <GlassButton variant="secondary" onClick={onCancel} disabled={loading}>
                Cancel &amp; add redirects
            </GlassButton>
            <GlassButton variant="primary" onClick={onProceed} disabled={loading}>
                {loading ? 'Checking…' : 'Publish anyway'}
            </GlassButton>
        </div>
    );

    const totalAtRisk = summary ? summary.missing_count + summary.changed_count : 0;
    const windowDays = summary?.window_days ?? 90;

    return (
        <GlassModal
            isOpen={isOpen}
            onClose={onCancel}
            title="Migration Risk Detected"
            footer={footer}
            size="md"
        >
            <div className="sa-mig-modal-body">
                {loading ? (
                    <p className="sa-mig-modal-warning">
                        Checking for ranking pages that would be affected…
                    </p>
                ) : summary && summary.has_risk ? (
                    <>
                        {/* Warning summary */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                            <AlertCircle
                                size={18}
                                style={{ color: 'var(--danger, #ef4444)', flexShrink: 0, marginTop: 2 }}
                            />
                            <p className="sa-mig-modal-warning">
                                Publishing this site will remove{' '}
                                <strong>{totalAtRisk} page{totalAtRisk !== 1 ? 's' : ''}</strong> that
                                earned{' '}
                                <strong>{(summary.lost_clicks ?? 0).toLocaleString()} clicks</strong> in
                                the last {windowDays} days from search results.
                                Add 301 redirects from the old URLs to keep their rankings.
                            </p>
                        </div>

                        {/* Top at-risk pages */}
                        {summary.top_missing.length > 0 && (
                            <div className="sa-mig-modal-list">
                                {summary.top_missing.map((p, i) => (
                                    <div key={i} className="sa-mig-modal-list-item">
                                        <span className="sa-mig-modal-url" title={p.raw_url}>
                                            {p.raw_url}
                                        </span>
                                        <span className="sa-mig-modal-clicks">
                                            {p.clicks.toLocaleString()} clicks
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <p className="sa-mig-modal-note">
                            View the full Migration Check in the SEO module for redirect suggestions.
                            You can still publish now and add redirects afterwards.
                        </p>
                    </>
                ) : (
                    <p className="sa-mig-modal-warning">
                        No ranking pages are at risk. You can publish safely.
                    </p>
                )}
            </div>
        </GlassModal>
    );
};

export default PrePublishMigrationModal;
